import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

export function OfflineBanner(): React.JSX.Element | null {
    if (Platform.OS !== 'web') {
        return null;
    }
    return <OfflineBannerInner />;
}

function OfflineBannerInner(): React.JSX.Element | null {
    const { isOnline, wasOffline, retrySync } = useNetworkStatus();
    const [showReconnected, setShowReconnected] = useState(false);

    useEffect(() => {
        if (isOnline && wasOffline) {
            setShowReconnected(true);
            const timer = setTimeout(() => {
                setShowReconnected(false);
            }, 3500);
            return () => clearTimeout(timer);
        }
    }, [isOnline, wasOffline]);

    if (isOnline && !showReconnected) {
        return null;
    }

    if (showReconnected) {
        return (
            <View style={styles.outerContainer} pointerEvents="box-none">
                <View
                    style={[styles.banner, styles.onlineBanner]}
                    accessibilityRole="status"
                    accessibilityLiveRegion="polite"
                >
                    <Text style={styles.onlineText} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        ✨ Back online! Your work has been synced.
                    </Text>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.outerContainer} pointerEvents="box-none">
            <View
                style={[styles.banner, styles.offlineBanner]}
                accessibilityRole="status"
                accessibilityLiveRegion="polite"
            >
                <Text style={styles.offlineText} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                    📡 Working offline — your work is safely saved!
                </Text>
                <TouchableOpacity
                    style={styles.retryButton}
                    onPress={retrySync}
                    activeOpacity={0.8}
                    accessibilityRole="button"
                    accessibilityLabel="Retry connecting"
                >
                    <Text style={styles.retryText} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        Retry
                    </Text>
                </TouchableOpacity>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    outerContainer: {
        position: 'absolute',
        top: 12,
        left: 16,
        right: 16,
        zIndex: 9999,
        alignItems: 'center',
    },
    banner: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 24,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.12,
        shadowRadius: 6,
        elevation: 4,
        maxWidth: 560,
        width: '100%',
    },
    offlineBanner: {
        backgroundColor: '#FEF3C7',
        borderWidth: 2,
        borderColor: '#F59E0B',
    },
    offlineText: {
        color: '#92400E',
        fontSize: 14,
        fontWeight: '700',
        flex: 1,
        textAlign: 'center',
    },
    onlineBanner: {
        backgroundColor: '#DCFCE7',
        borderWidth: 2,
        borderColor: '#22C55E',
    },
    onlineText: {
        color: '#166534',
        fontSize: 14,
        fontWeight: '700',
        textAlign: 'center',
    },
    retryButton: {
        backgroundColor: '#F59E0B',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 14,
        marginLeft: 10,
    },
    retryText: {
        color: '#FFFFFF',
        fontSize: 13,
        fontWeight: '800',
    },
});
